/**
 * La sessione nel browser: dove sta il refresh token e come la condividono
 * le schede.
 *
 * Il refresh token sta in `sessionStorage`, che è di una scheda sola: una
 * scheda aperta con la rotellina o "Apri in una nuova scheda" nasce vuota e
 * risultava disconnessa. Ora, senza token, una scheda lo chiede alle altre
 * del portale su un `BroadcastChannel` (stessa origine) e la prima che ha una
 * sessione risponde. Le schede condividono così la stessa sessione sul server
 * (`backend/auth/sessioni.py`): l'uscita da una chiude tutte quelle della
 * stessa sessione.
 *
 * L'impersonazione resta della sua scheda: chi impersona risponde con la
 * propria sessione (il token di ritorno dell'operatore), non con quella del
 * cliente impersonato.
 */

export const REFRESH_KEY = 'dtk_refresh_token';
/** Il refresh token dell'operatore durante un'impersonazione (`AdminContext`). */
export const ADMIN_REFRESH_KEY = 'dtk_admin_refresh_token';
export const IMPERSONATED_KEY = 'dtk_impersonated_client';
const LEGACY_ADMIN_ACCESS_KEY = 'dtk_admin_access_token';

const CANALE = 'dtk-sessione';
/** Quanto aspetta una scheda nuova la risposta delle altre: rispondono in
 *  pochi millisecondi, se ci sono. */
const ATTESA_MS = 200;
/** In `localStorage`: qualche scheda di questo browser ha avuto una sessione
 *  e non ne è uscita. Senza, una scheda nuova non chiede nulla e non aspetta
 *  (un visitatore anonimo). Nessun token qui, solo il segno. */
const SEGNO_KEY = 'dtk_sessione_aperta';

type Messaggio =
  | { tipo: 'chiedi'; id: string }
  | { tipo: 'sessione'; id: string; token: string }
  | { tipo: 'uscita'; sid: string };

function leggiChiave(k: string): string | null {
  try { return sessionStorage.getItem(k); } catch { return null; }
}
function scriviChiave(k: string, v: string | null): void {
  try {
    if (v === null) sessionStorage.removeItem(k); else sessionStorage.setItem(k, v);
  } catch { /* storage non disponibile: la sessione vive solo in memoria */ }
}

export const leggi = () => leggiChiave(REFRESH_KEY);
export function salva(token: string): void {
  scriviChiave(REFRESH_KEY, token);
  try { localStorage.setItem(SEGNO_KEY, '1'); } catch { /* niente segno: si chiederà comunque */ }
}

/** Toglie tutto ciò che questa scheda sa della sessione, impersonazione compresa. */
export function cancella(): void {
  for (const k of [REFRESH_KEY, ADMIN_REFRESH_KEY, IMPERSONATED_KEY, LEGACY_ADMIN_ACCESS_KEY]) scriviChiave(k, null);
}

/** Il token della sessione di questa scheda come la vede l'utente: durante
 *  un'impersonazione è quello dell'operatore. */
export function tokenProprio(): string | null {
  return leggiChiave(ADMIN_REFRESH_KEY) ?? leggi();
}

/** L'id della sessione sul server (claim `sid`), senza verificare la firma:
 *  serve solo a riconoscere le schede della stessa sessione. */
export function sidDi(token: string | null): string | null {
  if (!token) return null;
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64)).sid ?? null;
  } catch {
    return null;
  }
}

const invia = (canale: BroadcastChannel, m: Messaggio) => canale.postMessage(m);

function apriCanale(): BroadcastChannel | null {
  try {
    return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(CANALE);
  } catch {
    return null;
  }
}

/**
 * Ascolta le altre schede: risponde a chi chiede una sessione e chiama
 * `onUscita` quando la sessione di questa scheda viene chiusa altrove.
 * Rende la funzione che smette di ascoltare.
 */
export function ascolta(onUscita: () => void): () => void {
  const canale = apriCanale();
  if (!canale) return () => {};
  canale.onmessage = (e: MessageEvent<Messaggio>) => {
    const m = e.data;
    if (m?.tipo === 'chiedi') {
      const token = tokenProprio();
      if (token) invia(canale, { tipo: 'sessione', id: m.id, token });
    } else if (m?.tipo === 'uscita') {
      const mie = [sidDi(leggi()), sidDi(leggiChiave(ADMIN_REFRESH_KEY))];
      if (m.sid && mie.includes(m.sid)) onUscita();
    }
  };
  return () => canale.close();
}

/** Chiede la sessione alle altre schede aperte; null se nessuna risponde. */
export function chiediAlleAltreSchede(): Promise<string | null> {
  let segno: string | null = '1';
  try { segno = localStorage.getItem(SEGNO_KEY); } catch { /* si chiede */ }
  if (!segno) return Promise.resolve(null);
  const canale = apriCanale();
  if (!canale) return Promise.resolve(null);
  const id = Math.random().toString(36).slice(2);
  return new Promise(resolve => {
    const fine = (token: string | null) => {
      clearTimeout(timer);
      canale.close();
      resolve(token);
    };
    const timer = setTimeout(() => fine(null), ATTESA_MS);
    canale.onmessage = (e: MessageEvent<Messaggio>) => {
      if (e.data?.tipo === 'sessione' && e.data.id === id) fine(e.data.token);
    };
    invia(canale, { tipo: 'chiedi', id });
  });
}

/** Avvisa le altre schede che la sessione `sid` è chiusa. */
export function annunciaUscita(sid: string | null): void {
  try { localStorage.removeItem(SEGNO_KEY); } catch { /* resta: costa solo l'attesa */ }
  if (!sid) return;
  const canale = apriCanale();
  if (!canale) return;
  invia(canale, { tipo: 'uscita', sid });
  canale.close();
}
